import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useQueryCurrencies } from '@/modules/shared/query-client/useQueryCurrencies'
import AccomodationRowItem from './AccomodationRowItem'
import AccomodationRowNoData from './AccomodationRowNoData'
import AccomodationActionBar from './AccomodationActionBar'

interface BusinessTripEditAccommodationsProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  onGenerateAccommodations?: () => void
  companyId: string
  validationErrors?: Record<string, any>
  wrongIndices?: Set<number>
}

const getAccommodationsColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  { title: t('common.date'), key: 'date', type: 'string', sort: false },
  { title: t('common.city'), key: 'city', type: 'string', sort: false },
  { title: t('common.country'), key: 'country', type: 'string', sort: false },
  {
    title: t('businessTrip.col-amount'),
    key: 'amount',
    type: 'number',
    sort: false,
  },
  {
    title: t('businessTrip.col-currency'),
    key: 'currency',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-exchange-rate'),
    key: 'exchangeRate',
    type: 'number',
    sort: false,
  },
  {
    title: t('businessTrip.col-exchange-rate-date'),
    key: 'exchangeRateDate',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-table'),
    key: 'table',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-invoice-number'),
    key: 'invoiceNumber',
    type: 'string',
    sort: false,
  },
  {
    title: t('businessTrip.col-amount-pln'),
    key: 'plnAmount',
    type: 'number',
    sort: false,
  },
]

const BusinessTripEditAccommodations: React.FC<
  BusinessTripEditAccommodationsProps
> = ({
  tripData,
  onUpdate,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItems,
  onGenerateAccommodations,
  companyId,
  validationErrors = {},
}) => {
  const { t } = useLocalize()
  const AccommodationsColumns = getAccommodationsColumns(t)

  const { data: currencies = [] } = useQueryCurrencies({ companyId })

  const [searchValue, setSearchValue] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [sortConfig, setSortConfig] = React.useState<{
    key: string
    direction: 'asc' | 'desc'
  } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const accommodations = tripData?.accommodations || []

  const filteredAccommodations = React.useMemo(() => {
    let result = accommodations

    if (searchValue) {
      const search = searchValue.toLowerCase()
      result = result.filter((acc: any) => {
        return (
          acc.city?.toLowerCase().includes(search) ||
          acc.country?.name?.toLowerCase().includes(search) ||
          acc.invoiceNumber?.toLowerCase().includes(search) ||
          acc.table?.toLowerCase().includes(search)
        )
      })
    }

    if (sortConfig) {
      result = [...result].sort((a: any, b: any) => {
        let aValue: any
        let bValue: any

        switch (sortConfig.key) {
          case 'date':
            aValue = a.date ? new Date(a.date).getTime() : 0
            bValue = b.date ? new Date(b.date).getTime() : 0
            break
          case 'city':
            aValue = (a.city || '').toLowerCase()
            bValue = (b.city || '').toLowerCase()
            break
          case 'country':
            aValue = a.country?.name?.toLowerCase() || ''
            bValue = b.country?.name?.toLowerCase() || ''
            break
          case 'amount':
            aValue = Number(a.amount) || 0
            bValue = Number(b.amount) || 0
            break
          case 'currency':
            aValue = (a.currency?.code || a.currency || '')
              .toString()
              .toLowerCase()
            bValue = (b.currency?.code || b.currency || '')
              .toString()
              .toLowerCase()
            break
          case 'exchangeRate':
            aValue = Number(a.exchangeRate) || 0
            bValue = Number(b.exchangeRate) || 0
            break
          case 'exchangeRateDate':
            aValue = a.exchangeRateDate
              ? new Date(a.exchangeRateDate).getTime()
              : 0
            bValue = b.exchangeRateDate
              ? new Date(b.exchangeRateDate).getTime()
              : 0
            break
          case 'table':
            aValue = (a.table || '').toLowerCase()
            bValue = (b.table || '').toLowerCase()
            break
          case 'invoiceNumber':
            aValue = (a.invoiceNumber || '').toLowerCase()
            bValue = (b.invoiceNumber || '').toLowerCase()
            break
          case 'plnAmount':
            aValue = Number(a.plnAmount) || 0
            bValue = Number(b.plnAmount) || 0
            break
          default:
            return 0
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [accommodations, searchValue, sortConfig])

  const getOriginalIndex = (acc: any): number => {
    const idx = accommodations.indexOf(acc)
    if (idx !== -1) return idx
    return accommodations.findIndex((a: any) => {
      if (a.id != null && acc.id != null && String(a.id) === String(acc.id))
        return true
      if (
        a.lp != null &&
        acc.lp != null &&
        a.lp !== -1 &&
        acc.lp !== -1 &&
        a.lp === acc.lp
      )
        return true
      return false
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const indices = filteredAccommodations
        .map((a: any) => getOriginalIndex(a))
        .filter((i) => i !== -1)
      setSelectedIds(indices)
    } else {
      setSelectedIds([])
    }
  }

  const toggleId = (id: number) => {
    if (id === -1) return
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    )
  }

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const isSelectedAny = selectedIds.length > 0

  return (
    <div
      ref={containerRef}
      className="tw-rounded-lg  tw-h-full  tw-bg-white tw-pb-4"
    >
      {validationErrors.accommodations && (
        <div className="tw-mx-2 tw-mb-4 tw-rounded-lg tw-border tw-border-red-300 tw-bg-red-50 tw-p-3">
          <p className="tw-text-sm tw-font-medium tw-text-red-800">
            {validationErrors.accommodations}
          </p>
        </div>
      )}

      <AccomodationActionBar
        selectedIds={selectedIds}
        onUpdate={onUpdate}
        currencies={currencies}
        onAddArrayItem={onAddArrayItem}
        setSelectedIds={setSelectedIds}
        accommodations={accommodations}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        filteredAccommodationsLength={filteredAccommodations.length}
        onGenerateAccommodations={onGenerateAccommodations}
      />

      <div className="tw-max-h-[70vh] tw-h-full tw-overflow-auto tw-rounded-lg tw-border tw-border-base-300">
        <table className="tw-w-full tw-min-w-[1600px] tw-table-auto tw-border-collapse">
          <TableHeader
            columns={AccommodationsColumns}
            fontSize="md"
            isSelectedAny={isSelectedAny}
            maxWidthOff
            rounded
            sortConfig={sortConfig}
            bolder
            disabledToggle={false}
            toggleAll={toggleAll}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {filteredAccommodations.length > 0 ? (
              filteredAccommodations.map((acc: any, index: number) => {
                const originalIndex = getOriginalIndex(acc)
                const isChecked = selectedIds.includes(originalIndex)

                return (
                  <AccomodationRowItem
                    currencies={currencies}
                    onUpdateArrayItem={onUpdateArrayItem}
                    companyId={companyId}
                    acc={acc}
                    accommodations={accommodations}
                    isChecked={isChecked}
                    toggleId={toggleId}
                    originalIndex={originalIndex}
                    key={originalIndex !== -1 ? originalIndex : `temp-${index}`}
                  />
                )
              })
            ) : (
              <AccomodationRowNoData
                colSpan={AccommodationsColumns.length + 1}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BusinessTripEditAccommodations
